import React, { useEffect, useState } from 'react';
import { Button, Col, Divider, message, Modal, Row, Select, Spin } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'lucide-react';
import { get, post } from '../../utils/fetch';
import styles from './JiraUserMappingModal.module.css';

type OptionType = { value: string; label: string; email?: string; originalLabel?: string };
type Mapping = { rowId: string; clickupUserId: string; bugherdUserId: string };
type BugherdUser = {
  id: string | number;
  display_name?: string;
  name?: string;
  email?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string | number;
  integrationId: string | null;
  projectIntegrationId: string | null;
  workspaceId: string | null;
  listId: string | null;
  bugherdUsers: BugherdUser[];
  container?: HTMLElement | (() => HTMLElement);
}

let nextMappingRowId = 0;
const newMappingRowId = () => `clickup-user-mapping-${nextMappingRowId++}`;
const newEmptyMapping = (): Mapping => ({
  rowId: newMappingRowId(),
  clickupUserId: '',
  bugherdUserId: ''
});

const ClickupUserMappingModal: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  integrationId,
  projectIntegrationId,
  workspaceId,
  listId,
  bugherdUsers,
  container
}) => {
  const [loading, setLoading] = useState(false);
  const [clickupUsers, setClickupUsers] = useState<OptionType[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([newEmptyMapping()]);
  const hasListId = !!listId && listId !== 'undefined';

  useEffect(() => {
    if (open && integrationId && hasListId && projectIntegrationId) {
      loadData();
    }

    if (open && (!integrationId || !hasListId || !projectIntegrationId)) {
      message.warning('Please save a ClickUp list before managing assigned user mapping.');
    }
  }, [open, integrationId, hasListId, workspaceId, listId, projectIntegrationId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchClickupUsers(), fetchCurrentSettings()]);
    setLoading(false);
  };

  const fetchClickupUsers = async () => {
    try {
      const workspaceQuery = workspaceId ? `&workspace_id=${workspaceId}` : '';
      const results = await get(
        `/organizations/${organizationId}/integrations/clickup_users?id=${integrationId}${workspaceQuery}&list_id=${listId}`
      );
      setClickupUsers(
        results.map((user: OptionType) => ({
          ...user,
          label: user.email ? `${user.label} (${user.email})` : user.label,
          originalLabel: user.label
        }))
      );
    } catch (error) {
      message.error('Failed to fetch ClickUp users.');
    }
  };

  const fetchCurrentSettings = async () => {
    try {
      const result = await get(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${projectIntegrationId}`
      );
      const initialMappings = Object.entries(result?.settings?.user_mapping || {}).map(
        ([clickupUserId, bugherdUserId]) => ({
          rowId: newMappingRowId(),
          clickupUserId,
          bugherdUserId: String(bugherdUserId)
        })
      );
      setMappings(initialMappings.length > 0 ? initialMappings : [newEmptyMapping()]);
    } catch (error) {
      setMappings([newEmptyMapping()]);
    }
  };

  const saveMappings = async (currentMappings: Mapping[]) => {
    try {
      const usedClickupUserIds = new Set<string>();
      const usedBugherdUserIds = new Set<string>();
      const userMappingPayload = currentMappings.reduce((acc, mapping) => {
        if (
          mapping.clickupUserId &&
          mapping.bugherdUserId &&
          !usedClickupUserIds.has(mapping.clickupUserId) &&
          !usedBugherdUserIds.has(mapping.bugherdUserId)
        ) {
          acc[mapping.clickupUserId] = mapping.bugherdUserId;
          usedClickupUserIds.add(mapping.clickupUserId);
          usedBugherdUserIds.add(mapping.bugherdUserId);
        }
        return acc;
      }, {} as Record<string, string>);

      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/save_settings?integration_code=Clickup`,
        {
          project_integration_id: projectIntegrationId,
          settings: {
            user_mapping: userMappingPayload
          }
        }
      );
    } catch (error) {
      message.error('Failed to save user mappings.');
    }
  };

  const handleAddMapping = () => {
    setMappings([...mappings, newEmptyMapping()]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mappings.filter((_, i) => i !== index);
    setMappings(newMappings);
    saveMappings(newMappings);
  };

  const handleMappingChange = (
    index: number,
    field: 'clickupUserId' | 'bugherdUserId',
    value?: string
  ) => {
    const newMappings = mappings.map((mapping, currentIndex) =>
      currentIndex === index ? { ...mapping, [field]: value || '' } : mapping
    );
    setMappings(newMappings);

    if (newMappings[index].clickupUserId && newMappings[index].bugherdUserId) {
      saveMappings(newMappings);
    }
  };

  const handleAutoMap = () => {
    const autoPairs: Mapping[] = [];

    clickupUsers.forEach(clickupUser => {
      const remoteLabel = (clickupUser.originalLabel || clickupUser.label).toLowerCase();
      const matchedBugherdUser = bugherdUsers.find(user =>
        (clickupUser.email && user.email?.toLowerCase() === clickupUser.email.toLowerCase()) ||
        user.display_name?.toLowerCase() === remoteLabel ||
        user.name?.toLowerCase() === remoteLabel ||
        user.email?.toLowerCase() === remoteLabel
      );

      if (matchedBugherdUser) {
        autoPairs.push({
          rowId: newMappingRowId(),
          clickupUserId: clickupUser.value,
          bugherdUserId: String(matchedBugherdUser.id)
        });
      }
    });

    if (autoPairs.length > 0) {
      setMappings(autoPairs);
      saveMappings(autoPairs);
      message.success(`Successfully mapped ${autoPairs.length} users.`);
    } else {
      message.info('No matching users found by name or email.');
    }
  };

  const bugherdUserOptions = bugherdUsers.map(user => ({
    label: user.display_name || user.name || user.email,
    value: String(user.id)
  }));

  const bugherdUserOptionsForMapping = (index: number) => {
    const selectedUserIds = mappings
      .filter((_mapping, mappingIndex) => mappingIndex !== index)
      .map(mapping => mapping.bugherdUserId)
      .filter(Boolean);

    return bugherdUserOptions.filter(
      option => !selectedUserIds.includes(option.value)
    );
  };

  const clickupUserOptionsForMapping = (index: number) => {
    const selectedUserIds = mappings
      .filter((_mapping, mappingIndex) => mappingIndex !== index)
      .map(mapping => mapping.clickupUserId)
      .filter(Boolean);

    return clickupUsers.filter(
      option => !selectedUserIds.includes(option.value)
    );
  };

  const renderMappingList = () => (
    <div className={styles.mappingList}>
      <div className={styles.mappingRowsContainer}>
        <Row gutter={16} className={styles.headerRow}>
          <Col span={10}><strong>BugHerd User</strong></Col>
          <Col span={2}></Col>
          <Col span={10}><strong>ClickUp User</strong></Col>
          <Col span={2}></Col>
        </Row>
        {mappings.map((mapping, index) => (
          <Row key={mapping.rowId} gutter={16} className={styles.mappingRow} align="middle">
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search BugHerd user"
                optionFilterProp="label"
                value={mapping.bugherdUserId || undefined}
                onChange={value => handleMappingChange(index, 'bugherdUserId', value)}
                options={bugherdUserOptionsForMapping(index)}
                allowClear
              />
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <Link size={16} className={styles.linkIcon} />
            </Col>
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search ClickUp user"
                optionFilterProp="label"
                value={mapping.clickupUserId || undefined}
                onChange={value => handleMappingChange(index, 'clickupUserId', value)}
                options={clickupUserOptionsForMapping(index)}
                allowClear
              />
            </Col>
            <Col span={2}>
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveMapping(index)}
                danger
              />
            </Col>
          </Row>
        ))}
        <div className={styles.addMapping}>
          <Button type="link" onClick={handleAddMapping} icon={<PlusOutlined />}>
            Sync another user
          </Button>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Divider plain>or</Divider>
        <Button type="primary" size="large" onClick={handleAutoMap} style={{ padding: '0 40px' }}>
          Automap BugHerd to ClickUp users
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title="Manage assigned users between BugHerd and ClickUp"
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
      getContainer={container}
    >
      <div className={styles.modalContent}>
        <p>Match BugHerd assigned users to ClickUp users so synced tasks are assigned correctly in ClickUp.</p>
        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : renderMappingList()}
      </div>
    </Modal>
  );
};

export default ClickupUserMappingModal;
